/**
 * メッセージアダプター
 *
 * 各データソース（ShotComment, DM 等）を共通 MessageData 型に変換する関数群。
 * MessageItem コンポーネントに統一的にデータを渡すために使用する。
 */

import { ShotComment, ShotCommentFile } from "../app/services/shotComments";
import { DmMessage, DmMessageFile } from "../app/services/dmMessages";
import { ChatMessage } from "../app/services/chatMessages";
import { SoremonMessage } from "../app/services/soremon";
import { MessageData, MessageFile, MessageUser } from "../types/message";

// ─── ヘルパー ─────────────────────────────────────────

/** MIME タイプまたはカテゴリ文字列を MessageFile の file_type カテゴリに正規化する */
function normalizeFileType(
  fileType: string | null | undefined
): "image" | "video" | "audio" | "document" | "other" {
  // null/undefined の場合は "other" を返す
  if (!fileType) return "other";

  // 既にカテゴリ形式ならそのまま返す
  const categories = ["image", "video", "audio", "document", "other"] as const;
  if (categories.includes(fileType as any)) {
    return fileType as (typeof categories)[number];
  }

  // MIME タイプ形式（例: "audio/wav", "image/png"）からカテゴリを判定
  const lower = fileType.toLowerCase();
  if (lower.startsWith("image/")) return "image";
  if (lower.startsWith("video/")) return "video";
  if (lower.startsWith("audio/")) return "audio";
  if (lower === "application/pdf" || lower.startsWith("application/pdf"))
    return "document";

  return "other";
}

/** ShotCommentFile を共通 MessageFile 型に変換する */
function shotCommentFileToMessageFile(file: ShotCommentFile): MessageFile {
  return {
    id: file.id,
    original_url: file.original_url,
    file_name: file.file_name,
    file_type: normalizeFileType(file.file_type),
    file_metadata: file.file_metadata,
    folder: file.folder,
    downloaded_users: file.downloaded_users,
    optimized_url: file.optimized_url,
    waveform_url: file.waveform_url,
    has_waveform: file.has_waveform,
    thumbnail: file.thumbnail,
    acr_scan_status: file.acr_scan_status,
    acr_scan_result: file.acr_scan_result,
    acr_scanned_at: file.acr_scanned_at,
  };
}

// ─── アダプター ───────────────────────────────────────

/**
 * ShotComment を共通 MessageData 型に変換する
 *
 * フィールド名の差異を吸収:
 *   - ShotComment.comment      → MessageData.message
 *   - ShotComment.files        → MessageFile[]（file_type を正規化）
 *   - ShotComment.media_item_id → MessageData.media_item_id（ピン先メディア）
 *   - ShotComment.timecode_start → MessageData.timecode_start（旧環境互換の再生開始位置）
 *   - ShotComment.pin_thumbnail_url → MessageData.pin_thumbnail_url（ピン時刻フレーム画像）
 */
export function shotCommentToMessageData(comment: ShotComment): MessageData {
  return {
    id: comment.id,
    author: {
      id: comment.author.id,
      display_name: comment.author.display_name,
      avatar: comment.author.avatar,
      position: comment.author.position,
      seen_at: comment.author.seen_at,
    },
    parent_id: comment.parent_id,
    message: comment.comment,
    is_resolved: comment.is_resolved,
    has_files: comment.has_files,
    files: (comment.files || []).map(shotCommentFileToMessageFile),
    receivers: (comment.receivers || []).map((r) => ({
      id: r.id,
      display_name: r.display_name,
      avatar: r.avatar,
      position: r.position,
      seen_at: r.seen_at,
    })),
    created_at: comment.created_at,
    updated_at: comment.updated_at,
    media_item_id: comment.media_item_id,
    timecode_start: comment.timecode_start,
    pin_thumbnail_url: comment.pin_thumbnail_url,
  };
}

// ─── DM メッセージアダプター ─────────────────────────

/** DmMessageFile を共通 MessageFile 型に変換する */
function dmFileToMessageFile(file: DmMessageFile): MessageFile {
  return {
    id: file.id,
    original_url: file.original_url ?? "",
    download_url: file.download_url ?? null,
    file_name: file.file_name ?? "",
    file_type: normalizeFileType(file.file_type),
    file_metadata: file.file_metadata ?? undefined,
    folder: file.folder ?? undefined,
    downloaded_users: file.downloaded_users,
    optimized_url: file.optimized_url,
    waveform_url: file.waveform_url,
    has_waveform: file.has_waveform,
    thumbnail: file.thumbnail,
    // null = ACR 非対応（個別 Chat の添付等）。共通型では undefined に正規化し、
    // ACR バッジ類を表示しない。
    acr_scan_status: file.acr_scan_status ?? undefined,
    acr_scan_result: file.acr_scan_result,
    acr_scanned_at: file.acr_scanned_at,
  };
}

/**
 * DmMessage を共通 MessageData 型に変換する
 *
 * DM 固有フィールドの変換:
 *   - DmMessage.type           → MessageData.type（"user" / "system"）
 *   - DmMessage.system_content → MessageData.system_content
 *   - DmMessage.author         → MessageData.author（null の場合あり: システムメッセージ）
 *   - DmMessage.files          → MessageFile[]（file_type を正規化）
 */
export function dmMessageToMessageData(msg: DmMessage): MessageData {
  return {
    id: msg.id,
    author: msg.author
      ? {
          id: msg.author.id,
          display_name: msg.author.display_name,
          avatar: msg.author.avatar,
          position: msg.author.position ?? undefined,
          seen_at: msg.author.seen_at,
        }
      : null,
    parent_id: msg.parent_id,
    message: msg.message,
    is_resolved: msg.is_resolved,
    has_files: msg.has_files,
    files: (msg.files || []).map(dmFileToMessageFile),
    receivers: (msg.receivers || []).map((r) => ({
      id: r.id,
      display_name: r.display_name,
      avatar: r.avatar,
      position: r.position ?? undefined,
      seen_at: r.seen_at,
    })),
    created_at: msg.created_at,
    updated_at: msg.updated_at,
    type: msg.type,
    system_content: msg.system_content,
  };
}

// ─── Chat (DM) メッセージアダプター ───────────────────

/**
 * ChatMessage（オファー非依存のシンプル DM メッセージ）を共通 MessageData 型に
 * 変換する。ステータス・契約・システムメッセージは持たないため、
 * `is_resolved` は常に false、`type` は "user"。
 * ファイル添付は DM と同一 payload 形式のため `dmFileToMessageFile` で変換する。
 */
export function chatMessageToMessageData(msg: ChatMessage): MessageData {
  return {
    id: msg.id,
    author: msg.author
      ? {
          id: msg.author.id,
          display_name: msg.author.display_name,
          avatar: msg.author.avatar,
          position: msg.author.position ?? undefined,
          seen_at: msg.author.seen_at,
        }
      : null,
    parent_id: msg.parent_id,
    message: msg.message,
    is_resolved: false,
    has_files: msg.has_files ?? false,
    files: (msg.files || []).map(dmFileToMessageFile),
    receivers: (msg.receivers || []).map((r) => ({
      id: r.id,
      display_name: r.display_name,
      avatar: r.avatar,
      position: r.position ?? undefined,
      seen_at: r.seen_at,
    })),
    created_at: msg.created_at,
    updated_at: msg.updated_at,
    type: "user",
    system_content: null,
  };
}

// ─── soremon（AI アシスタント）メッセージアダプター ─────────

/**
 * SoremonMessage を共通 MessageData 型に変換する。
 * assistant の発言は `soremon`（左側・アバター付き）、user の発言は `me`
 * （右側。MessageItem は isOurs で扱う）を投稿者にする。既読・返信は持たない。
 */
export function soremonMessageToMessageData(
  msg: Pick<SoremonMessage, "id" | "role" | "content" | "created" | "files">,
  authors: { soremon: MessageUser; me: MessageUser },
): MessageData {
  const files: MessageFile[] = (msg.files ?? []).map((file) => ({
    id: file.id,
    file_name: file.file_name,
    file_type: file.file_type,
    original_url: file.original_url,
    download_url: file.download_url,
  }));
  return {
    id: String(msg.id),
    author: msg.role === "assistant" ? authors.soremon : authors.me,
    parent_id: null,
    message: msg.content,
    is_resolved: false,
    has_files: files.length > 0,
    files,
    receivers: [],
    created_at: msg.created,
    updated_at: msg.created,
    type: "user",
    system_content: null,
  };
}
